/**
 * Toasts, with undo.
 *
 * The design rule this exists to serve: destructive and semi-destructive
 * actions get an undo toast, never a confirmation dialog. A dialog interrupts
 * before you have done anything and costs you the thought you were holding;
 * undo interrupts nothing and is there if you were wrong.
 */

import { create } from 'zustand';

export interface Toast {
  id: number;
  message: string;
  /** Present when the action can be reversed. Rendered as an Undo button. */
  onUndo?: () => void;
  tone: 'neutral' | 'success' | 'warning' | 'danger';
  /** Milliseconds before auto-dismiss. */
  duration: number;
}

interface ToastStore {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id' | 'tone' | 'duration'> & Partial<Toast>) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],

  push: (input) => {
    const id = nextId++;
    const toast: Toast = {
      id,
      tone: 'neutral',
      // Undoable toasts stay longer: 3s is not enough time to notice a mistake,
      // decide, and move the pointer.
      duration: input.onUndo ? 8000 : 4000,
      ...input,
    };

    set((state) => ({
      // Newest first, capped. A stack of twelve toasts is noise, not feedback.
      toasts: [toast, ...state.toasts].slice(0, 4),
    }));

    if (toast.duration > 0) {
      setTimeout(() => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
      }, toast.duration);
    }

    return id;
  },

  dismiss: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

export const toast = {
  show: (message: string, options: Partial<Toast> = {}) =>
    useToastStore.getState().push({ message, ...options }),

  undoable: (message: string, onUndo: () => void) =>
    useToastStore.getState().push({ message, onUndo }),

  error: (message: string) =>
    useToastStore.getState().push({ message, tone: 'danger' }),
};
